import { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import type { Dayjs } from "dayjs";
import { DatePickerCalendarAlternateIcon } from "../../../components/shared/DatePickerCalendarAlternateIcon";
import { FilterBarDateRangePopover } from "../../../components/RegulationsTable/FilterBar/FilterBarDateRangePopover";
import {
  aiPlanFilterBarSx,
  aiPlanFilterFieldSx,
} from "../../../constants/regulationDetailsNavTabStyles";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";

const dateFieldSx = {
  flex: "0 0 auto",
  width: 240,
  maxWidth: "100%",
} as const;

function renderFilterPlaceholder(placeholder: string) {
  return (
    <Box component="span" sx={{ color: "#9ca3af" }}>
      {placeholder}
    </Box>
  );
}

function renderSelectValue(
  selected: unknown,
  options: ReadonlyArray<{ code: string; name: string }>,
  placeholder: string,
) {
  const value = String(selected ?? "");
  if (!value) return renderFilterPlaceholder(placeholder);
  return options.find((option) => option.code === value)?.name ?? value;
}

type TriageResultFilterBarProps = Readonly<{
  objectType: string;
  objectSubType: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onObjectTypeChange: (objectType: string, objectSubType: string) => void;
  onObjectSubTypeChange: (objectSubType: string) => void;
  onDateRangeChange: (from: Dayjs | null, to: Dayjs | null) => void;
}>;

function formatDateRangeDisplay(
  fromDate: Dayjs | null,
  toDate: Dayjs | null,
): string {
  if (fromDate && toDate) {
    return `${fromDate.format("DD MMM YYYY")} - ${toDate.format("DD MMM YYYY")}`;
  }
  if (fromDate) return `From: ${fromDate.format("DD MMM YYYY")}`;
  if (toDate) return `To: ${toDate.format("DD MMM YYYY")}`;
  return "";
}

export default function TriageResultFilterBar({
  objectType,
  objectSubType,
  fromDate,
  toDate,
  onObjectTypeChange,
  onObjectSubTypeChange,
  onDateRangeChange,
}: TriageResultFilterBarProps) {
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const {
    objectTypeOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
  } = useAssessmentPlanFilterOptions(objectType);

  const objectSubTypeOptions = getObjectSubTypeOptions(objectType);
  const showObjectSubType = !objectType || objectSubTypeOptions.length > 0;

  return (
    <Box sx={aiPlanFilterBarSx}>
      <TextField
        select
        size="small"
        value={objectType}
        disabled={planTypesLoading}
        sx={aiPlanFilterFieldSx}
        onChange={(event) => {
          onObjectTypeChange(event.target.value, "");
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) =>
            renderSelectValue(
              selected,
              objectTypeOptions,
              "Select the Object Type",
            ),
          MenuProps: {
            PaperProps: { sx: { maxHeight: 300 } },
          },
        }}
        inputProps={{ "aria-label": "Object Type" }}
      >
        <MenuItem value="">All</MenuItem>
        {objectTypeOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>

      {showObjectSubType ? (
        <TextField
          select
          size="small"
          value={objectSubType}
          sx={aiPlanFilterFieldSx}
          onChange={(event) => onObjectSubTypeChange(event.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) =>
              renderSelectValue(
                selected,
                objectSubTypeOptions,
                "Select the Object Sub-Type",
              ),
            MenuProps: {
              PaperProps: { sx: { maxHeight: 300 } },
            },
          }}
          inputProps={{ "aria-label": "Object Sub-Type" }}
        >
          <MenuItem value="">All</MenuItem>
          {objectSubTypeOptions.map((option) => (
            <MenuItem key={option.code || "all"} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      <TextField
        size="small"
        value={formatDateRangeDisplay(fromDate, toDate)}
        placeholder="Start date - End date"
        onClick={(event) => setDateAnchorEl(event.currentTarget)}
        sx={dateFieldSx}
        slotProps={{
          input: {
            readOnly: true,
            "aria-label": "Date range",
          },
          inputLabel: { shrink: false },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: -1 }}>
              <IconButton
                aria-label="Select date range"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setDateAnchorEl(event.currentTarget);
                }}
                sx={{
                  borderLeft: "1px solid #d1d5db",
                  borderRadius: 0,
                  px: 1,
                }}
              >
                <DatePickerCalendarAlternateIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FilterBarDateRangePopover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        fromDate={fromDate}
        toDate={toDate}
        onClose={() => setDateAnchorEl(null)}
        onDateRangeChange={onDateRangeChange}
      />
    </Box>
  );
}
